import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapTrifold } from "@phosphor-icons/react";

const COR_PLACA = "#0A0A0A";
const COR_PINTURA = "#FFCC00";
const COR_OUTRO = "#71717A";

const fmtData = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

function FitBounds({ pontos }) {
  const map = useMap();
  useEffect(() => {
    if (pontos.length === 1) map.setView([pontos[0].latitude, pontos[0].longitude], 15);
    else if (pontos.length > 1) map.fitBounds(pontos.map((p) => [p.latitude, p.longitude]), { padding: [30, 30] });
  }, [map, pontos]);
  return null;
}

function PopupApontamento({ a }) {
  const p = a.placa || a.pintura || {};
  return (
    <Popup>
      <div style={{ fontSize: "0.8rem", minWidth: 180 }} data-testid={`mapa-popup-${a.id}`}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          {a.placa ? `Placa ${p.codigo}${p.nome ? " · " + p.nome : ""}` : a.pintura ? `Pintura · ${p.tipo_label || p.tipo || ""}` : "Apontamento"}
        </div>
        <div>{fmtData(a.data_execucao)}</div>
        {a.placa && <div>km {p.km || "—"} · {p.lado_via || "—"}</div>}
        {a.pintura && <div>km {p.km_inicial} → {p.km_final} · {p.extensao_m || 0} m</div>}
        {a.placa && p.area_total_m2 != null && <div>{Number(p.area_total_m2).toLocaleString("pt-BR", { maximumFractionDigits: 3 })} m²</div>}
        {a.pintura && <div>{Number(p.area_m2 || a.quantidade || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} m² · {p.cor || ""}</div>}
        {a.fotos && a.fotos.length > 0 && <img src={(a.fotos_thumb || a.fotos)[0]} alt="" loading="lazy" style={{ width: "100%", marginTop: 6, borderRadius: 3 }} />}
        <a href={`${window.location.origin}/api/public/apontamento/${a.id}`} target="_blank" rel="noreferrer" style={{ display: "block", marginTop: 6, textDecoration: "underline" }}>Ver apontamento</a>
      </div>
    </Popup>
  );
}

export default function MapaOS({ apontamentos }) {
  const [filtro, setFiltro] = useState("todos");
  const pontos = useMemo(
    () => (apontamentos || []).filter((a) => !a.deleted && a.latitude != null && a.longitude != null),
    [apontamentos],
  );
  const placas = pontos.filter((a) => a.placa);
  const pinturas = pontos.filter((a) => a.pintura);
  const outros = pontos.filter((a) => !a.placa && !a.pintura);
  const visiveis = filtro === "placas" ? placas : filtro === "pintura" ? pinturas : pontos;
  const semGps = (apontamentos || []).filter((a) => !a.deleted).length - pontos.length;
  const linhaPintura = [...pinturas]
    .sort((a, b) => (a.pintura?.km_inicial || "").localeCompare(b.pintura?.km_inicial || ""))
    .map((a) => [a.latitude, a.longitude]);

  const Chip = ({ id, label, cor, n }) => (
    <button
      type="button"
      className="chip"
      onClick={() => setFiltro(id)}
      data-testid={`mapa-filtro-${id}`}
      style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, border: filtro === id ? "2px solid #0A0A0A" : "1px solid #E4E4E7", background: filtro === id ? "#FAFAFA" : "#FFF" }}
    >
      {cor && <span style={{ width: 10, height: 10, borderRadius: "50%", background: cor, border: "1px solid #0A0A0A", display: "inline-block" }} />}
      {label} <span className="font-mono">{n}</span>
    </button>
  );

  return (
    <div className="pcp-card no-print" style={{ padding: 20, marginBottom: 24 }} data-testid="mapa-os">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <h2 className="font-display font-bold text-lg" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MapTrifold size={20} weight="bold" /> Mapa da OS
        </h2>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Chip id="todos" label="Todos" n={pontos.length} />
          <Chip id="placas" label="Placas" cor={COR_PLACA} n={placas.length} />
          <Chip id="pintura" label="Pintura" cor={COR_PINTURA} n={pinturas.length} />
        </div>
      </div>
      {pontos.length === 0 ? (
        <div style={{ padding: 32, textAlign: "center", color: "#71717A", fontSize: "0.85rem" }} data-testid="mapa-vazio">
          Nenhum apontamento com GPS registrado nesta OS ainda.
        </div>
      ) : (
        <div style={{ height: 420, borderRadius: 4, overflow: "hidden", border: "1px solid #E4E4E7" }}>
          <MapContainer center={[pontos[0].latitude, pontos[0].longitude]} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds pontos={visiveis.length ? visiveis : pontos} />
            {filtro !== "placas" && linhaPintura.length > 1 && (
              <Polyline positions={linhaPintura} pathOptions={{ color: COR_PINTURA, weight: 4, opacity: 0.7, dashArray: "8 6" }} />
            )}
            {visiveis.map((a) => (
              <CircleMarker
                key={a.id}
                center={[a.latitude, a.longitude]}
                radius={a.placa ? 8 : 7}
                pathOptions={{ color: "#0A0A0A", weight: 1.5, fillColor: a.placa ? COR_PLACA : a.pintura ? COR_PINTURA : COR_OUTRO, fillOpacity: 0.9 }}
              >
                <PopupApontamento a={a} />
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      )}
      <div style={{ marginTop: 8, fontSize: "0.75rem", color: "#71717A" }} data-testid="mapa-rodape">
        {pontos.length} apontamento(s) georreferenciado(s){outros.length > 0 ? ` · ${outros.length} de outros serviços` : ""}{semGps > 0 ? ` · ${semGps} sem GPS (não exibidos)` : ""}. Clique em um ponto para ver detalhes e foto.
      </div>
    </div>
  );
}
